export interface SkinsPlayer {
  id: string;
  gross: (number | null)[];
  net: (number | null)[];
}

export interface SkinsHoleResult {
  hole: number;
  winner: string | null;
  value: number;
  carried: boolean;
}

export interface SkinsResult {
  skinsByHole: SkinsHoleResult[];
  totals: Record<string, number>;
  net: Record<string, number>;
  entry: number;
  carryUnclaimed: number;
}

export interface NassauHoleResult {
  winner: string | null;
  s1: number | null;
  s2: number | null;
}

export interface NassauLegResult {
  p1w: number;
  p2w: number;
  winner: string | null;
  diff: number;
}

export interface NassauResult {
  holes: NassauHoleResult[];
  front: NassauLegResult;
  back: NassauLegResult;
  total: NassauLegResult;
  net: Record<string, number>;
  bet: number;
}

export function computeSkins(
  players: SkinsPlayer[],
  mode: 'net' | 'gross' = 'net',
  perHole = 1,
  carryover = true,
): SkinsResult {
  const skinsByHole: SkinsHoleResult[] = [];
  let carry = 0;
  for (let h = 0; h < 18; h++) {
    const scores = players
      .map((p) => ({
        id: p.id,
        score: mode === 'net' ? p.net[h] : p.gross[h],
      }))
      .filter((s) => Number.isFinite(s.score));
    if (scores.length < 2) {
      skinsByHole.push({ hole: h + 1, winner: null, value: 0, carried: carryover && carry > 0 });
      continue;
    }
    const min = Math.min(...scores.map((s) => s.score as number));
    const winners = scores.filter((s) => s.score === min);
    if (winners.length === 1) {
      const value = perHole * players.length * (1 + (carryover ? carry : 0));
      skinsByHole.push({ hole: h + 1, winner: winners[0].id, value, carried: carryover && carry > 0 });
      carry = 0;
    } else if (carryover) {
      carry += 1;
      skinsByHole.push({ hole: h + 1, winner: null, value: 0, carried: true });
    } else {
      skinsByHole.push({ hole: h + 1, winner: null, value: 0, carried: false });
    }
  }
  const totals: Record<string, number> = {};
  players.forEach((p) => { totals[p.id] = 0; });
  skinsByHole.forEach((s) => { if (s.winner) totals[s.winner] += s.value; });
  const entry = 18 * perHole;
  const net: Record<string, number> = {};
  players.forEach((p) => { net[p.id] = totals[p.id] - entry; });
  return { skinsByHole, totals, net, entry, carryUnclaimed: carry };
}

export function computeNassau(
  p1: SkinsPlayer,
  p2: SkinsPlayer,
  mode: 'net' | 'gross' = 'net',
  bet = 5,
): NassauResult {
  const holeCount = Math.max(p1.gross.length, p2.gross.length, 1);
  const holes: NassauHoleResult[] = [];
  for (let h = 0; h < holeCount; h++) {
    const s1 = mode === 'net' ? p1.net[h] : p1.gross[h];
    const s2 = mode === 'net' ? p2.net[h] : p2.gross[h];
    if (!Number.isFinite(s1) || !Number.isFinite(s2)) {
      holes.push({ winner: null, s1, s2 });
      continue;
    }
    if ((s1 as number) < (s2 as number)) holes.push({ winner: p1.id, s1, s2 });
    else if ((s2 as number) < (s1 as number)) holes.push({ winner: p2.id, s1, s2 });
    else holes.push({ winner: null, s1, s2 });
  }
  const tally = (slice: NassauHoleResult[]): NassauLegResult => {
    const p1w = slice.filter((h) => h.winner === p1.id).length;
    const p2w = slice.filter((h) => h.winner === p2.id).length;
    let winner: string | null = null;
    if (p1w > p2w) winner = p1.id;
    else if (p2w > p1w) winner = p2.id;
    return { p1w, p2w, winner, diff: Math.abs(p1w - p2w) };
  };
  const front = tally(holes.slice(0, Math.min(9, holeCount)));
  const back = holeCount > 9 ? tally(holes.slice(9, holeCount)) : { p1w: 0, p2w: 0, winner: null, diff: 0 };
  const total = tally(holes);
  const owedBy: Record<string, number> = { [p1.id]: 0, [p2.id]: 0 };
  [front, back, total].forEach((leg) => {
    if (leg.winner === p1.id) owedBy[p2.id] += bet;
    else if (leg.winner === p2.id) owedBy[p1.id] += bet;
  });
  const net: Record<string, number> = {
    [p1.id]: owedBy[p2.id] - owedBy[p1.id],
    [p2.id]: owedBy[p1.id] - owedBy[p2.id],
  };
  return { holes, front, back, total, net, bet };
}
